import { NativeSelectOption } from "@bednbody/ui/components/native-select";
import { NativeSelect } from "@bednbody/ui/components/native-select";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Callout } from "@bednbody/ui/components/callout";
// Was the distance we paid on actually measured? — 0199, the reader half.
//
// ⚠ 0199 SHIPPED THE MEASUREMENT AND NOTHING READ IT. Three columns have been
// filled by `end_trip` since it landed and no screen anywhere showed them, so
// the Open Item the migration exists to inform — what to pay for a trip whose
// track has holes in it — still could not be asked with numbers. Its own header
// says the track-to-chord ratio "needs no GPS expertise to read on an ops
// screen". This is that screen.
//
// ⚠ IT DIAGNOSES AND STOPS THERE. No button, no recompute, no "approve
// corrected distance". 0199: "Paying a road-network estimate instead of the
// measured track would make client-side conditions payable, which SECURITY §5
// and money-math.md §3 both forbid; paying the chord is what we do today and it
// underpays. Both are defensible and the choice changes rider earnings, so it
// belongs to Luigi."
//
// ⚠ AND THERE IS NO ₱ COLUMN, WHICH IS THE HARDEST PART OF THIS DESIGN.
// The obvious ops screen shows "₱ owed to riders". It cannot exist honestly:
// the distance travelled through a gap is precisely what nobody measured, so
// any peso figure would rest on an assumed distance — the invented rule 0199
// refused to make. Distances and counts only.

import { Badge } from "@bednbody/ui/components/badge";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { StatTile } from "@bednbody/ui/components/stat-tile";
import type { ColumnDef } from "@tanstack/react-table";
import { CheckCircleIcon, MapPinLineIcon, WarningIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import {
  rankByConcern,
  summarise,
  trackToChordRatio,
  trackingVerdict,
  verdictLabel,
  verdictTone,
  type TripTracking,
} from "../tracking-quality";
import { useTripTrackingQuality } from "../mileage.hooks";

const RANGES = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
] as const;

const km = (n: number) => `${n.toFixed(2)} km`;

function GapCell({ trip }: { trip: TripTracking }) {
  if (!trip.measured) return <span className="text-muted-foreground">—</span>;
  if (trip.trackingGaps === 0) return <span className="text-muted-foreground">none</span>;
  // Minutes, because "412 s" is a number an ops reader has to convert before it
  // means anything, and the threshold it is being judged against is one minute.
  const mins = Math.round(trip.maxGapSeconds / 60);
  return (
    <span>
      {trip.trackingGaps} · worst {mins} min
    </span>
  );
}

function RatioCell({ trip }: { trip: TripTracking }) {
  const ratio = trackToChordRatio(trip);
  if (ratio === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={ratio <= 1.05 ? "font-medium text-warning" : undefined}>
      {ratio.toFixed(2)}×
    </span>
  );
}

export function TrackingQualitySection() {
  const [days, setDays] = useState<number>(30);
  const { data, isLoading, isError } = useTripTrackingQuality(days);

  const trips = useMemo(() => rankByConcern(data ?? []), [data]);
  const summary = useMemo(() => summarise(data ?? []), [data]);
  const columns = useMemo<ColumnDef<TripTracking, unknown>[]>(
    () => [
      {
        accessorKey: "riderName",
        header: "Rider",
        cell: ({ row }) => (
          <AdminTableIdentity
            title={row.original.riderName ?? "Unknown rider"}
            subtitle={
              row.original.endedAt
                ? new Date(row.original.endedAt).toLocaleString("en-PH", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })
                : "End time unavailable"
            }
            fallback={(row.original.riderName ?? "R").slice(0, 2).toUpperCase()}
          />
        ),
      },
      {
        accessorKey: "totalKm",
        header: "Measured",
        cell: ({ row }) => <span className="tabular-nums">{km(row.original.totalKm)}</span>,
      },
      {
        accessorKey: "straightLineKm",
        header: "Straight line",
        cell: ({ row }) => (
          <span className="tabular-nums text-muted-foreground">
            {row.original.straightLineKm === null ? "—" : km(row.original.straightLineKm)}
          </span>
        ),
      },
      {
        id: "ratio",
        accessorFn: (trip) => trackToChordRatio(trip),
        header: "Ratio",
        cell: ({ row }) => <RatioCell trip={row.original} />,
      },
      {
        accessorKey: "trackingGaps",
        header: "Gaps",
        cell: ({ row }) => <GapCell trip={row.original} />,
      },
      {
        id: "verdict",
        accessorFn: (trip) => trackingVerdict(trip),
        header: "Verdict",
        cell: ({ row }) => {
          const verdict = trackingVerdict(row.original);
          return <Badge variant={verdictTone(verdict)}>{verdictLabel(verdict)}</Badge>;
        },
      },
      {
        id: "actions",
        enableSorting: false,
        header: "",
        cell: ({ row }) => (
          <AdminTableRowMenu
            label={row.original.riderName ?? "trip"}
            actions={[
              {
                label: "Copy trip ID",
                onSelect: () => void navigator.clipboard.writeText(row.original.id),
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  return (
    <section className="mt-8 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">Distance measurement</h2>
          <p className="text-sm text-muted-foreground">
            Rider pay is computed from the track the app recorded. When the app is backgrounded —
            which happens every time a rider opens Waze — the track jumps straight from the last
            point to the next one, and the distance comes out short.{" "}
            <span className="font-medium text-foreground">
              This is a measurement report, not a repricing tool.
            </span>{" "}
            What a gapped trip should pay is undecided.
          </p>
        </div>
        <NativeSelect value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {RANGES.map((r) => (
            <NativeSelectOption key={r.days} value={r.days}>
              {r.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>

      {isError ? (
        <Callout tone="danger" role="alert">
          Couldn&apos;t load trip tracking — check your connection and try again.
        </Callout>
      ) : isLoading ? (
        <Skeleton className="h-40 motion-reduce:animate-none" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <StatTile
              icon={CheckCircleIcon}
              tone="success"
              layout="compact"
              label="Measured throughout"
            >
              <p className="text-2xl font-semibold tabular-nums text-success">{summary.healthy}</p>
            </StatTile>
            <StatTile
              icon={WarningIcon}
              tone="warning"
              layout="compact"
              label="Gapped, above flat fare"
            >
              <p className="text-2xl font-semibold tabular-nums text-warning">
                {summary.gappedUnpaid}
              </p>
            </StatTile>
            <StatTile icon={MapPinLineIcon} layout="compact" label="Gapped, inside flat fare">
              <p className="text-2xl font-semibold tabular-nums">{summary.gappedFree}</p>
            </StatTile>
            <StatTile icon={MapPinLineIcon} layout="compact" label="Not measured">
              <p className="text-2xl font-semibold tabular-nums">{summary.notMeasured}</p>
            </StatTile>
          </div>

          {/* ⚠ THE ONE SENTENCE THAT STOPS THIS BEING MISREAD. A short trip
              cannot lose anybody money — the fare below 5 km is flat ₱40 — so
              the two gapped counts above are different problems, and an ops
              reader must not average them into one worry. */}
          <p className="text-sm text-muted-foreground">
            Only gaps on trips over 5 km can cost a rider anything: below that the dispatch fee is a
            flat ₱40 regardless of distance. A track-to-straight-line ratio near 1.00× means the
            recorded path has collapsed onto a straight line — the clearest sign a trip was never
            really measured.
          </p>

          <AdminDataTable
            presentation="standalone"
            data={trips}
            columns={columns}
            getRowId={(trip) => trip.id}
            searchText={(trip) =>
              `${trip.riderName ?? ""} ${verdictLabel(trackingVerdict(trip))} ${trip.id}`
            }
            searchPlaceholder="Search riders, verdicts, or trip IDs…"
            facets={[
              {
                id: "verdict",
                label: "Measurement verdict",
                getValue: (trip) => trackingVerdict(trip),
                options: [
                  { value: "healthy", label: "Measured" },
                  { value: "gapped_unpaid", label: "Gapped · above fare" },
                  { value: "gapped_free", label: "Gapped · flat fare" },
                  { value: "not_measured", label: "Not measured" },
                ],
              },
            ]}
            exportFilename={`tracking-quality-${days}-days`}
            exportColumns={[
              { header: "Trip ID", value: (trip) => trip.id },
              { header: "Rider", value: (trip) => trip.riderName },
              { header: "Ended", value: (trip) => trip.endedAt },
              { header: "Measured km", value: (trip) => trip.totalKm },
              { header: "Straight-line km", value: (trip) => trip.straightLineKm },
              { header: "Track/chord ratio", value: (trip) => trackToChordRatio(trip) },
              { header: "Tracking gaps", value: (trip) => trip.trackingGaps },
              { header: "Worst gap seconds", value: (trip) => trip.maxGapSeconds },
              { header: "Verdict", value: (trip) => verdictLabel(trackingVerdict(trip)) },
            ]}
            emptyTitle="No completed trips in this range"
            emptyDescription="Try a longer reporting range."
            initialSorting={[{ id: "verdict", desc: false }]}
          />
        </>
      )}
    </section>
  );
}
